import { llmLabel } from "./homePageApi";

/**
 * A combination's `llms[]` lists one entry per exact model VERSION
 * (customerLlmId) that ever ran it — so when a customer's config moves
 * from, say, gpt-5.2 to gpt-5.4 under the same OpenAI account, that shows
 * up as two separate entries. Trusting BE's per-exact-version `llmLatest[]`
 * for "latest vs previous" then goes blind right at that boundary — the
 * newer version's own `previous` is null even though the account has a
 * perfectly good prior run, just under the old version's id. The old
 * Home page's real alert engine avoids exactly this by matching LLMs
 * across runs by PROVIDER, not exact name (see MONITORING_HOMEPAGE_BE_SPEC.md
 * §4.4). This file ports that same idea here: group by provider, and
 * derive latest/previous/full history straight from `combo.runs` (the
 * raw ground truth) rather than the version-scoped `llmLatest[]`.
 */
export function providerLabel(provider) {
  if (!provider) return "Unknown";
  return provider.replace(/-/g, " ");
}

/**
 * Returns one entry per provider present in `combo.llms`:
 *   { provider, label, domain, customerLlmIds, customerLlmId, name,
 *     series: [{runDate, customerLlmId, llmName, brands, indexScore,
 *               visibilityScore, sentimentScore}, ...],   // oldest -> newest
 *     latest: {runDate, indexScore, visibilityScore, sentimentScore} | null,
 *     previous: {...} | null }
 * `series` carries every run's full brand list (main + competitors) for
 * the chart; `latest`/`previous` are the main brand's own scores, for the
 * summary card / table / insights. A provider with zero runs for this
 * combination is dropped.
 */
export function deriveProviderLlms(combo) {
  if (!combo) return [];

  const groups = new Map();
  (combo.llms || []).forEach((l) => {
    if (!groups.has(l.provider)) {
      groups.set(l.provider, {
        provider: l.provider,
        label: providerLabel(l.provider),
        domain: l.domain,
        customerLlmIds: new Set(),
      });
    }
    groups.get(l.provider).customerLlmIds.add(l.customerLlmId);
  });

  const runs = combo.runs || [];

  return [...groups.values()]
    .map((group) => {
      const series = [];
      for (const run of runs) {
        const entry = run.llms.find((l) => group.customerLlmIds.has(l.customerLlmId));
        if (!entry) continue;
        const mainBrand = entry.brands.find((b) => b.isMainBrand);
        series.push({
          runDate: run.runDate,
          customerLlmId: entry.customerLlmId,
          llmName: llmLabel(entry.llmName),
          brands: entry.brands,
          indexScore: mainBrand?.indexScore ?? null,
          visibilityScore: mainBrand?.visibilityScore ?? null,
          sentimentScore: mainBrand?.sentimentScore ?? null,
        });
      }
      const latestPoint = series[series.length - 1] || null;
      const previousPoint = series.length > 1 ? series[series.length - 2] : null;
      return {
        provider: group.provider,
        label: group.label,
        domain: group.domain,
        customerLlmIds: [...group.customerLlmIds],
        // Whichever exact model version actually produced the latest run,
        // labelled via OVERVIEW_LLM_LABELS (same as every other module) —
        // shown in the summary table so the specific launched model stays
        // visible even though selection/grouping is by provider.
        customerLlmId: latestPoint?.customerLlmId ?? [...group.customerLlmIds][0],
        name: latestPoint?.llmName ?? group.label,
        series,
        latest: latestPoint
          ? { runDate: latestPoint.runDate, indexScore: latestPoint.indexScore, visibilityScore: latestPoint.visibilityScore, sentimentScore: latestPoint.sentimentScore }
          : null,
        previous: previousPoint
          ? { runDate: previousPoint.runDate, indexScore: previousPoint.indexScore, visibilityScore: previousPoint.visibilityScore, sentimentScore: previousPoint.sentimentScore }
          : null,
      };
    })
    .filter((g) => g.latest);
}
